import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { UserIcon, EditIcon, ShieldCheckIcon, ShieldIcon, CheckIcon, XIcon, SearchIcon, FilterIcon, ChevronDownIcon, ChevronRightIcon, UsersIcon, CrownIcon } from 'lucide-react';
import { toast } from 'sonner';
import RolePermissionsSettings from './RolePermissionsSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserPermissionsSettings = () => {
  // Tab Management
  const [activeTab, setActiveTab] = useState('users');
  
  const [users, setUsers] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Search and Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [selectedPermissionFilter, setSelectedPermissionFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUsers(),
        fetchAvailablePermissions()
      ]);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/user-permissions/company-users`);
      // Ensure users is always an array
      setUsers(Array.isArray(response.data.users) ? response.data.users : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Kullanıcılar yüklenirken hata oluştu');
      setUsers([]);
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const response = await axios.get(`${API}/user-permissions/available`);
      setAvailablePermissions(response.data.permissions);
    } catch (error) {
      toast.error('Yetkiler yüklenirken hata oluştu');
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      const response = await axios.get(`${API}/user-permissions/${userId}`);
      const permissions = response.data.permissions;
      
      // Convert permissions array to object structure for easier management
      const permissionsByCategory = {
        modules: {},
        approval_actions: {},
        finance_approval_actions: {},
        finance_actions: {},
        payment_actions: {},
        closing_actions: {},
        data_access: {},
        administrative: {},
        company_management: {},
        reporting: {}
      };
      
      // Include ALL permissions, both granted and not granted
      permissions.forEach(permission => {
        permissionsByCategory[permission.permission_category][permission.permission_key] = permission.is_granted;
      });
      
      setUserPermissions(permissionsByCategory);
    } catch (error) {
      toast.error('Kullanıcı yetkileri yüklenirken hata oluştu');
      setUserPermissions({ modules: {}, approval_actions: {}, finance_approval_actions: {}, finance_actions: {}, payment_actions: {}, closing_actions: {}, data_access: {}, administrative: {}, company_management: {}, reporting: {} });
    }
  };

  const handleOpenPermissionDialog = async (user) => {
    setSelectedUser(user);
    await fetchUserPermissions(user.id);
    setPermissionDialogOpen(true);
  };

  const handlePermissionChange = (category, permissionKey, value) => {
    setUserPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permissionKey]: value
      }
    }));
  };

  const handleToggleAllInCategory = (category) => {
    const categoryPermissions = availablePermissions[category] || {};
    const allGranted = Object.keys(categoryPermissions).every(key => 
      userPermissions[category] && userPermissions[category][key]
    );
    
    const newCategoryPerms = {};
    Object.keys(categoryPermissions).forEach(key => {
      newCategoryPerms[key] = !allGranted;
    });
    
    setUserPermissions(prev => ({
      ...prev,
      [category]: newCategoryPerms
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/user-permissions/${selectedUser.id}/update`, userPermissions);
      toast.success('Kullanıcı yetkileri başarıyla güncellendi');
      setPermissionDialogOpen(false);
      fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yetkiler güncellenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyDefaultPermissions = async (user) => {
    if (!window.confirm(`${user.full_name} için pozisyon bazlı default yetkileri uygulamak istediğinizden emin misiniz?\n\nMevcut özel yetkiler silinecek ve ${user.role_name} pozisyonu için varsayılan yetkiler atanacak.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/user-permissions/${user.id}/apply-defaults`);
      toast.success(`${user.role_name} pozisyonu için default yetkiler uygulandı (${response.data.total_permissions} yetki)`);
      fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Default yetkiler uygulanırken hata oluştu');
    }
  };

  const getPermissionCount = (user) => {
    return user.total_permissions;
  };

  const getPermissionBadgeColor = (count) => {
    if (count === 0) return 'bg-gray-100 text-gray-600';
    if (count <= 3) return 'bg-yellow-100 text-yellow-700';
    if (count <= 6) return 'bg-blue-100 text-blue-700';
    return 'bg-green-100 text-green-700';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'modules': return '📱';
      case 'approval_actions': return '✅';
      case 'finance_approval_actions': return '💵';
      case 'payment_actions': return '💰';
      case 'closing_actions': return '📝';
      case 'data_access': return '👁️';
      case 'administrative': return '⚙️';
      case 'reporting': return '📊';
      default: return '🔒';
    }
  };

  const getCategoryTitle = (category) => {
    switch (category) {
      case 'modules': return 'Modül Erişimi';
      case 'approval_actions': return 'Onay Yetkileri';
      case 'finance_approval_actions': return 'Finans Onayı';
      case 'payment_actions': return 'Ödeme İşlemleri';
      case 'closing_actions': return 'Kapama İşlemleri';
      case 'data_access': return 'Veri Erişim Kapsamı';
      case 'administrative': return 'Yönetimsel Yetkiler';
      case 'reporting': return 'Raporlama Yetkileri';
      default: return category;
    }
  };

  const getCategoryDescription = (category) => {
    switch (category) {
      case 'modules': return 'Hangi sayfalara erişebilir';
      case 'approval_actions': return 'Kimler için onay verebilir';
      case 'finance_approval_actions': return 'Finans onayı verebilir';
      case 'payment_actions': return 'Ödeme yapabilir';
      case 'closing_actions': return 'Avans kapama işlemi yapabilir';
      case 'data_access': return 'Hangi verileri görebilir';
      case 'administrative': return 'Sistem yönetimi yapabilir';
      case 'reporting': return 'Rapor görüntüleme ve oluşturma';
      default: return '';
    }
  };

  // Filter and Search Functions
  const getFilteredUsers = () => {
    let filtered = [...users];

    // Search by name or email
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        user.full_name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    }

    // Filter by role
    if (selectedRoleFilter !== 'all') {
      filtered = filtered.filter(user => user.role_name === selectedRoleFilter);
    }

    // Filter by permission status
    if (selectedPermissionFilter !== 'all') {
      if (selectedPermissionFilter === 'with_permissions') {
        filtered = filtered.filter(user => user.has_custom_permissions);
      } else if (selectedPermissionFilter === 'no_permissions') {
        filtered = filtered.filter(user => !user.has_custom_permissions);
      }
    }

    return filtered;
  };

  const getGroupedUsers = () => {
    const filtered = getFilteredUsers();
    const grouped = {};
    
    filtered.forEach(user => {
      const role = user.role_name;
      if (!grouped[role]) {
        grouped[role] = [];
      }
      grouped[role].push(user);
    });

    return grouped;
  };

  const getUniqueRoles = () => {
    const roles = [...new Set(users.map(user => user.role_name))];
    return roles.sort();
  };

  const toggleGroup = (roleName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [roleName]: !prev[roleName]
    }));
  };

  // Initialize all groups as expanded
  useEffect(() => {
    if (users.length > 0) {
      const roles = getUniqueRoles();
      const initialExpanded = {};
      roles.forEach(role => {
        initialExpanded[role] = true;
      });
      setExpandedGroups(initialExpanded);
    }
  }, [users]);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Kullanıcı Yetkileri</h2>
          <p className="text-gray-600 mt-2">Yükleniyor...</p>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2 w-1/4"></div>
                    <div className="h-3 bg-gray-200 rounded mb-3 w-1/2"></div>
                    <div className="flex space-x-6">
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                </div>
                <div className="h-10 bg-gray-200 rounded w-32"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Kullanıcı Yetkileri</h2>
        <p className="text-gray-600 mt-2">
          Kullanıcılara özel yetki tanımlamaları yapın veya pozisyonlar için standart yetkiler belirleyin.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <UserIcon className="w-4 h-4" />
              <span>Kullanıcı Yetkileri</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'roles'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <CrownIcon className="w-4 h-4" />
              <span>Pozisyon Şablonları</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' ? (
        <div className="space-y-6">{/* Individual Users Tab Content */}

      {/* Search and Filter Section */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search Bar */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="İsim veya email ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          {/* Role Filter */}
          <div>
            <select
              value={selectedRoleFilter}
              onChange={(e) => setSelectedRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Pozisyonlar</option>
              {getUniqueRoles().map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {/* Permission Filter */}
          <div>
            <select
              value={selectedPermissionFilter}
              onChange={(e) => setSelectedPermissionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Yetki Durumları</option>
              <option value="with_permissions">Özel Yetkili</option>
              <option value="no_permissions">Standart Yetki</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-blue-700">
            <FilterIcon className="inline w-4 h-4 mr-1" />
            {getFilteredUsers().length} kullanıcı gösteriliyor
          </p>
          {(searchQuery || selectedRoleFilter !== 'all' || selectedPermissionFilter !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setSelectedRoleFilter('all');
                setSelectedPermissionFilter('all');
              }}
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
            >
              Filtreleri Temizle
            </Button>
          )}
        </div>
      </Card>

      {/* Grouped Users List */}
      <div className="space-y-6">
        {Object.entries(getGroupedUsers()).map(([roleName, roleUsers]) => (
          <Card key={roleName} className="border-2 border-gray-200 overflow-hidden">
            {/* Group Header */}
            <div 
              className="bg-gradient-to-r from-gray-100 to-gray-200 p-4 cursor-pointer hover:from-gray-200 hover:to-gray-300 transition-colors"
              onClick={() => toggleGroup(roleName)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <UsersIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{roleName}</h3>
                    <p className="text-sm text-gray-600">{roleUsers.length} kullanıcı</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700 px-3 py-1">
                    {roleUsers.filter(u => u.has_custom_permissions).length} özel yetkili
                  </Badge>
                  {expandedGroups[roleName] ? (
                    <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-gray-600" />
                  )}
                </div>
              </div>
            </div>

            {/* Group Users */}
            {expandedGroups[roleName] && (
              <div className="divide-y divide-gray-100">
                {roleUsers.map((user) => (
                  <div key={user.id} className="p-6 hover:bg-gray-50 transition-all duration-200">
            {/* User Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                  <UserIcon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{user.full_name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{user.email}</p>
                  <Badge 
                    variant="secondary" 
                    className="bg-blue-100 text-blue-700 px-3 py-1 text-sm font-medium rounded-full"
                  >
                    {user.role_name}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleApplyDefaultPermissions(user)}
                  className="text-green-600 border-green-300 hover:bg-green-50 flex items-center space-x-1"
                  title="Rol bazlı default yetkileri uygula"
                >
                  <ShieldCheckIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Default</span>
                </Button>
                <Button
                  onClick={() => handleOpenPermissionDialog(user)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 text-sm font-medium rounded-lg shadow-sm transition-all duration-200 flex items-center space-x-2"
                >
                  <EditIcon className="w-4 h-4" />
                  <span>Düzenle</span>
                </Button>
              </div>
            </div>
            
            {/* Permission Summary Grid */}
            <div className="grid grid-cols-4 gap-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
              <div className="text-center">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mb-2 mx-auto shadow-sm">
                  <span className="text-xl">📱</span>
                </div>
                <div className="text-xs text-gray-600 font-medium mb-1">Modüller</div>
                <div className="text-base font-bold text-gray-900">
                  {user.module_permissions}
                  <span className="text-sm text-gray-500 font-normal">/10</span>
                </div>
              </div>
              
              <div className="text-center">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mb-2 mx-auto shadow-sm">
                  <span className="text-xl">⚡</span>
                </div>
                <div className="text-xs text-gray-600 font-medium mb-1">İşlemler</div>
                <div className="text-base font-bold text-gray-900">
                  {user.action_permissions}
                  <span className="text-sm text-gray-500 font-normal">/9</span>
                </div>
              </div>
              
              <div className="text-center">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mb-2 mx-auto shadow-sm">
                  <span className="text-xl">🔒</span>
                </div>
                <div className="text-xs text-gray-600 font-medium mb-1">Toplam</div>
                <div className="text-base font-bold text-gray-900">
                  {getPermissionCount(user)}
                  <span className="text-sm text-gray-500 font-normal"> yetki</span>
                </div>
              </div>
              
              <div className="text-center">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 mx-auto shadow-sm ${
                  user.has_custom_permissions ? 'bg-green-100' : 'bg-gray-200'
                }`}>
                  <span className="text-xl">{user.has_custom_permissions ? '🌟' : '⚪'}</span>
                </div>
                <div className="text-xs text-gray-600 font-medium mb-1">Özel Yetki</div>
                <div className={`text-sm font-bold ${user.has_custom_permissions ? 'text-green-600' : 'text-gray-400'}`}>
                  {user.has_custom_permissions ? 'Aktif' : 'Yok'}
                </div>
              </div>
            </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Empty States */}
      {users.length === 0 && (
        <Card className="p-12 text-center">
          <ShieldIcon className="w-20 h-20 text-gray-300 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Henüz kullanıcı bulunmuyor</h3>
          <p className="text-gray-600 text-lg">Yetki tanımlamak için önce kullanıcı eklemelisiniz.</p>
        </Card>
      )}

      {users.length > 0 && Object.keys(getGroupedUsers()).length === 0 && (
        <Card className="p-12 text-center">
          <SearchIcon className="w-20 h-20 text-gray-300 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Arama kriterlerinize uygun kullanıcı bulunamadı</h3>
          <p className="text-gray-600 text-lg">Farklı filtreler deneyebilir veya arama teriminizi değiştirebilirsiniz.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSearchQuery('');
              setSelectedRoleFilter('all');
              setSelectedPermissionFilter('all');
            }}
          >
            Tüm Filtreleri Temizle
          </Button>
        </Card>
      )}

      {/* Permission Management Dialog */}
      <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <ShieldCheckIcon className="w-5 h-5 text-blue-600" />
              <span>
                {selectedUser?.full_name} - Yetki Yönetimi
              </span>
            </DialogTitle>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">{selectedUser.full_name}</h4>
                    <p className="text-sm text-gray-600">{selectedUser.email}</p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {selectedUser.role_name}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Permissions by Category */}
              {Object.entries(availablePermissions).map(([category, permissions]) => (
                <Card key={category} className="p-6 border-l-4 border-l-blue-500">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">{getCategoryIcon(category)}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {getCategoryTitle(category)}
                        </h3>
                        <p className="text-sm text-gray-600">{getCategoryDescription(category)}</p>
                        <Badge variant="secondary" className="text-xs mt-1">
                          {Object.keys(userPermissions[category] || {}).filter(key => 
                            userPermissions[category][key]
                          ).length} / {Object.keys(permissions).length} aktif
                        </Badge>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleAllInCategory(category)}
                      className="text-sm"
                    >
                      Tümünü Değiştir
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {Object.entries(permissions).map(([permissionKey, description]) => {
                      const parts = description.split(' - ');
                      const title = parts[0];
                      const subtitle = parts[1];
                      
                      return (
                        <div
                          key={permissionKey}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border border-gray-200"
                        >
                          <div className="flex-1">
                            <div className="font-semibold text-sm text-gray-900 mb-1">
                              {title}
                            </div>
                            <div className="text-sm text-gray-600">
                              {subtitle}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 font-mono">
                              {permissionKey}
                            </div>
                          </div>
                          <div className="ml-4">
                            <Button
                              variant={userPermissions[category] && userPermissions[category][permissionKey] ? "default" : "outline"}
                              size="sm"
                              onClick={() => handlePermissionChange(
                                category, 
                                permissionKey, 
                                !(userPermissions[category] && userPermissions[category][permissionKey])
                              )}
                              className={`${
                                userPermissions[category] && userPermissions[category][permissionKey]
                                  ? 'bg-green-600 hover:bg-green-700 text-white'
                                  : 'border-gray-300 hover:border-gray-400 text-gray-600'
                              }`}
                            >
                              {userPermissions[category] && userPermissions[category][permissionKey] ? (
                                <>
                                  <CheckIcon className="w-4 h-4 mr-1" />
                                  Aktif
                                </>
                              ) : (
                                <>
                                  <XIcon className="w-4 h-4 mr-1" />
                                  Pasif
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              ))}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setPermissionDialogOpen(false)}
                  disabled={saving}
                >
                  İptal
                </Button>
                <Button
                  onClick={handleSavePermissions}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? 'Kaydediliyor...' : 'Yetkileri Kaydet'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
      ) : (
        <RolePermissionsSettings />
      )}
    </div>
  );
};

export default UserPermissionsSettings;